// see: https://github.com/zeit/next.js/blob/canary/examples/with-apollo-auth/lib/withApollo.js
import React, { Component } from 'react';
import { getDataFromTree } from '@apollo/client/react/ssr';
import getBlazeUi from '@blaze-cms/core-ui';
import { setupCheckAccessToken } from '@blaze-cms/core-auth-ui';
import PropTypes from 'prop-types';
import dynamic from 'next/dynamic'; // Next.js dynamic
import { AppContext } from '@blaze-cms/nextjs-components';
import blazeConfig from '../blaze.config';
import { getErrorMessage } from '../helpers';
import HashObserver from '../components/HashObserver';

const ErrorBoundary = dynamic(() => import('@blaze-cms/core-errors-ui'));

const APP_LOG_NAMESPACE_MAIN = 'main';

function getWrappedApp(isSsr, config, blazeApp, App) {
  function getApolloClient(ctx, requestApp) {
    return ctx.apolloClient || requestApp.getClient() || requestApp.createClient();
  }

  function getRequestApp(requestApp) {
    return !requestApp && isSsr ? getBlazeUi({ config }) : blazeApp;
  }
  return class extends Component {
    static displayName = 'withBlaze(App)';

    static propTypes = {
      apolloState: PropTypes.object,
      apolloClient: PropTypes.object,
      processedBlazeEntities: PropTypes.object
    };

    static defaultProps = {
      apolloState: {},
      apolloClient: null,
      processedBlazeEntities: undefined
    };

    static async getInitialProps(context) {
      const { Component: _Component, router, AppTree, ctx } = context;
      let appProps = {};
      let errorProps = {};

      let requestApp = ctx.blazeApp;
      requestApp = getRequestApp(requestApp);

      requestApp.ctx.router = router;
      requestApp.ctx.req = ctx.req;
      requestApp.ctx.res = ctx.res;

      await blazeApp.init();
      await requestApp.init();

      const apolloClient = getApolloClient(ctx, requestApp);

      ctx.apolloClient = apolloClient;
      ctx.blazeApp = requestApp;

      requestApp.events.emit('page-load:get-initial-props:before', { ctx });

      if (App.getInitialProps) {
        appProps = await App.getInitialProps(context);
      }

      const { pageProps = {} } = appProps;
      const mainProps = {
        ...appProps,
        pageProps: { ...pageProps, ...errorProps }
      };

      if (!isSsr) return mainProps;

      try {
        const renderAndCheckPromises = [
          getDataFromTree(
            <AppTree
              {...appProps}
              Component={_Component}
              router={router}
              apolloClient={apolloClient}
            />
          ),
          requestApp.openQueryChecker.checkOpenQueries()
        ];
        // race is so that if getDataFromTree gets stuck the checkOpenQueries will resolve when outstanding queries are done
        await Promise.race(renderAndCheckPromises);
      } catch (error) {
        errorProps = getErrorMessage(error);
        if (errorProps.errorCode) ctx.res.statusCode = errorProps.errorCode;
        ctx.blazeApp.events.emit('monitor:log', { error });
        requestApp.log.error('Error while running `getDataFromTree`', error);
      }

      const apolloState = apolloClient.cache.extract();
      const sortedState = Object.keys(apolloState)
        .sort()
        .reduce((acc, key) => {
          acc[key] = apolloState[key];
          return acc;
        }, {});

      return {
        ...mainProps,
        pageProps: { ...mainProps.pageProps, ...errorProps },
        apolloState: sortedState,
        processedBlazeEntities: blazeApp.getProcessedEntities()
      };
    }

    constructor(props) {
      super(props);
      this.apolloClient = props.apolloClient;
      this.state = {
        appLoaded: isSsr || blazeApp.initComplete()
      };
      this.blazeApp = blazeApp;
    }

    async componentDidMount() {
      if (blazeApp.initComplete()) return;

      await blazeApp.init();

      this.setState({
        appLoaded: blazeApp.initComplete()
      });
    }

    setApolloClient() {
      if (!this.apolloClient) {
        const { apolloState, processedBlazeEntities } = this.props;
        this.apolloClient = blazeApp.createClient(apolloState, processedBlazeEntities);
        setupCheckAccessToken(this.apolloClient);
      }
    }

    render() {
      const { appLoaded } = this.state;

      if (!appLoaded && !isSsr) return '...loading';

      this.setApolloClient();

      return (
        <ErrorBoundary app={this.blazeApp}>
          <HashObserver />
          <AppContext.Provider value={{ blazeApp: this.blazeApp }}>
            <App {...this.props} apolloClient={this.apolloClient} blazeApp={this.blazeApp} />
          </AppContext.Provider>
        </ErrorBoundary>
      );
    }
  };
}

const withBlaze = (App, buildConfig, { forceSsr = false } = {}) => {
  const isSsr = forceSsr || typeof window === 'undefined';
  const config = buildConfig ? buildConfig(blazeConfig) : blazeConfig;
  const blazeApp = getBlazeUi({ config, logNameSpace: APP_LOG_NAMESPACE_MAIN });
  blazeApp.init();

  return getWrappedApp(isSsr, config, blazeApp, App);
};

export default withBlaze;
